import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AppContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen bg-black pt-28 pb-20 px-6">
      <div className="max-w-md mx-auto">
        <div className="text-center">
          <img src="/assets/emblem.png" className="w-16 mx-auto opacity-90" alt="" />
          <div className="bl-eyebrow mt-6 text-[color:var(--bl-gold)]">Inner Circle</div>
          <h1 className="bl-heading text-white text-4xl mt-3">{title}</h1>
          <p className="text-white/50 mt-3 text-sm">{sub}</p>
        </div>
        <div className="mt-10 bl-glass border border-white/10 p-8">{children}</div>
      </div>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.5 17.7 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.8c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.1-10 7.1-17.2z"/>
    <path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z"/>
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2 1.4-4.7 2.4-8.5 2.4-6.3 0-11.7-4-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/>
  </svg>
);

export const GoogleButton = ({ label = "Continue with Google" }) => (
  <>
    <button type="button" onClick={startGoogle} data-testid="google-signin"
      className="w-full bl-btn inline-flex items-center justify-center gap-3 bg-white/[0.03] hover:bg-white/[0.06]">
      <GoogleIcon /> {label}
    </button>
    <div className="flex items-center gap-3 text-white/30 font-mono text-[10px] tracking-[0.3em] uppercase">
      <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
    </div>
  </>
);

const inputCls = "w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-4 py-3 text-white font-mono text-sm";

export function Login() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const { login } = useAuth(); const nav = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("Welcome back"); nav("/account"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title="Sign In" sub="Access your member area.">
      <div className="space-y-4 mb-4"><GoogleButton /></div>
      <form onSubmit={onSubmit} className="space-y-4">
        <input data-testid="login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" className={inputCls} />
        <input data-testid="login-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" className={inputCls} />
        <button data-testid="login-submit" disabled={loading} className="w-full bl-btn primary disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
      <div className="mt-6 text-center text-white/50 text-sm space-y-2">
        <div>New to Bio Labz? <Link to="/register" className="text-[color:var(--bl-blue)]">Create an account</Link></div>
        <div><Link to="/forgot-password" data-testid="login-forgot-link" className="text-white/40 hover:text-[color:var(--bl-blue)] font-mono text-xs tracking-[0.2em] uppercase">Forgot password?</Link></div>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const [full_name, setName] = useState(""); const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const { register } = useAuth(); const nav = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(email, password, full_name); toast.success("Welcome to the Inner Circle"); nav("/account"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Registration failed"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title="Create Account" sub="Join the Inner Circle.">
      <div className="space-y-4 mb-4"><GoogleButton label="Sign up with Google" /></div>
      <form onSubmit={onSubmit} className="space-y-4">
        <input data-testid="register-name" required value={full_name} onChange={e=>setName(e.target.value)} placeholder="Full name" className={inputCls} />
        <input data-testid="register-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" className={inputCls} />
        <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password (min 6 chars)" className={inputCls} />
        <button data-testid="register-submit" disabled={loading} className="w-full bl-btn primary disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Create Account"}
        </button>
      </form>
      <div className="mt-6 text-center text-white/50 text-sm">
        Already registered? <Link to="/login" className="text-[color:var(--bl-blue)]">Sign in</Link>
      </div>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [loading, setLoading] = useState(false); const [sent, setSent] = useState(false);
  const { authApi } = useAuth();
  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await authApi.post("/auth/forgot-password", { email }); setSent(true); }
    catch { toast.error("Could not send reset link — try again"); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title="Reset Password" sub="We'll email you a one-hour link to choose a new password.">
      {sent ? (
        <div className="text-center space-y-3" data-testid="forgot-sent">
          <div className="bl-eyebrow text-[color:var(--bl-green)]">Check your inbox</div>
          <p className="text-white/70 text-sm">If an account exists for <span className="font-mono text-white">{email}</span>, a reset link is on its way.</p>
          <Link to="/login" className="inline-block mt-2 text-[color:var(--bl-blue)] text-sm">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <input data-testid="forgot-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" className={inputCls} />
          <button data-testid="forgot-submit" disabled={loading} className="w-full bl-btn primary disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Send reset link"}
          </button>
          <div className="text-center text-white/50 text-sm"><Link to="/login" className="text-[color:var(--bl-blue)]">Back to sign in</Link></div>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams(); const token = params.get("token") || "";
  const [password, setPassword] = useState(""); const [confirm, setConfirm] = useState(""); const [loading, setLoading] = useState(false);
  const { authApi, setUser } = useAuth(); const nav = useNavigate();
  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const r = await authApi.post("/auth/reset-password", { token, password });
      setUser(r.data.user); toast.success("Password updated — you're signed in"); nav("/account");
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
    finally { setLoading(false); }
  };
  if (!token) {
    return (
      <AuthShell title="Reset Password" sub="This link is missing its token.">
        <div className="text-center text-white/60 text-sm" data-testid="reset-missing-token">Request a new link from <Link to="/forgot-password" className="text-[color:var(--bl-blue)]">Forgot password</Link>.</div>
      </AuthShell>
    );
  }
  return (
    <AuthShell title="New Password" sub="Choose a new password for your account.">
      <form onSubmit={onSubmit} className="space-y-4">
        <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={e=>setPassword(e.target.value)} placeholder="New password (min 6 chars)" className={inputCls} />
        <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Confirm new password" className={inputCls} />
        <button data-testid="reset-submit" disabled={loading} className="w-full bl-btn primary disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}

// Lands here at {origin}/account#session_id=... — exchanges the one-time session_id server-side, then continues.
export function AuthCallback() {
  const { authApi, setUser } = useAuth(); const nav = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    authApi.post("/auth/google/session", { session_id: sessionId })
      .then(r => { setUser(r.data.user); toast.success(`Welcome, ${r.data.user.full_name.split(" ")[0]}`); nav("/account", { replace: true, state: { user: r.data.user } }); })
      .catch(() => { toast.error("Google sign-in failed — please try again"); nav("/login", { replace: true }); });
  }, [authApi, setUser, nav]);
  return (
    <div className="min-h-screen bg-black grid place-items-center" data-testid="auth-callback">
      <Loader2 className="animate-spin text-white/40" />
    </div>
  );
}
